/**
 * Subscription Plans Component
 * Displays subscription tiers with pricing and checkout integration
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useCredits } from '../../contexts/CreditsContext';
import { getApiBase } from '../../config/api';
import StarIcon from '@mui/icons-material/Star';
import PageHeader from '../PageHeader';
import './SubscriptionPlans.css';

interface SubscriptionPlan {
  id: string;
  name: string;
  slug: string;
  description: string;
  monthlyCredits: number;
  monthlyPrice: number;
  annualPrice: number;
  annualMonthlyEquivalent: number;
  annualSavingsPercent: number;
  features: string[];
  isFeatured: boolean;
  sortOrder: number;
}

interface ActiveSubscription {
  id: string;
  planSlug: string;
  planName: string;
  status: string;
  billingCycle: 'monthly' | 'annual';
  currentPeriodEnd: string;
  cancelAtPeriodEnd: boolean;
  monthlyCredits: number;
}

export const SubscriptionPlans: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { subscription: contextSubscription } = useCredits();
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [activeSub, setActiveSub] = useState<ActiveSubscription | null>(null);
  const [loading, setLoading] = useState(true);
  const [processingPlan, setProcessingPlan] = useState<string | null>(null);
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'annual'>('annual');
  const [error, setError] = useState<string | null>(null);
  
  // Check if user is in subscription management mode
  const urlParams = new URLSearchParams(window.location.search);
  const isManaging = urlParams.get('action') === 'manage';

  useEffect(() => {
    loadData();
  }, [user]);

  // Sync activeSub from CreditsContext subscription
  useEffect(() => {
    console.log('[SubscriptionPlans] CreditsContext subscription:', contextSubscription);
    console.log('[SubscriptionPlans] Full context subscription object:', JSON.stringify(contextSubscription, null, 2));
    
    if (contextSubscription && contextSubscription.status === 'active') {
      const synced = {
        id: contextSubscription.id,
        planSlug: contextSubscription.planSlug,
        planName: contextSubscription.planName,
        status: contextSubscription.status,
        billingCycle: contextSubscription.billingCycle,
        currentPeriodEnd: contextSubscription.currentPeriodEnd,
        cancelAtPeriodEnd: contextSubscription.cancelAtPeriodEnd,
        monthlyCredits: contextSubscription.monthlyCreditsAllocated,
      };
      console.log('[SubscriptionPlans] Setting activeSub:', synced);
      setActiveSub(synced);
    } else {
      console.log('[SubscriptionPlans] No active subscription, setting activeSub to null');
      setActiveSub(null);
    }
  }, [contextSubscription]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Load plans
      const plansResponse = await fetch(`${getApiBase()}/api/subscriptions/plans`);
      if (!plansResponse.ok) throw new Error('Failed to load plans');
      const plansData = await plansResponse.json();
      setPlans(plansData.plans);

      // Load active subscription if user is logged in
      if (user) {
        const subResponse = await fetch(`${getApiBase()}/api/subscriptions/active`, {
          headers: {
            'x-user-id': user.id,
          },
        });
        if (subResponse.ok) {
          const subData = await subResponse.json();
          setActiveSub(subData.subscription);
        }
      }
    } catch (err: any) {
      console.error('Failed to load subscription data:', err);
      setError('Failed to load subscription plans. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSubscribe = async (planSlug: string) => {
    if (!user) {
      // Could open auth modal here
      setError('Please sign in to subscribe');
      return;
    }

    try {
      setProcessingPlan(planSlug);
      setError(null);

      // Store original location to return to after subscription
      // If user came from a flow, they should go back there
      const originalUrl = document.referrer || sessionStorage.getItem('pre-subscription-url') || '/';
      sessionStorage.setItem('pre-subscription-url', originalUrl);

      const response = await fetch(`${getApiBase()}/api/subscriptions/create-checkout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-user-id': user.id,
          'x-user-email': user.email || '',
        },
        body: JSON.stringify({
          planSlug,
          billingCycle,
          returnUrl: window.location.origin + '/subscription/success',
          originalUrl, // Pass the original URL to return to
        }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to create checkout');
      }

      const { url } = await response.json();
      window.location.href = url;
    } catch (err: any) {
      console.error('Subscription failed:', err);
      setError(err.message || 'Failed to start checkout. Please try again.');
      setProcessingPlan(null);
    }
  };

  const handleManageSubscription = async () => {
    if (!user) return;

    try {
      setProcessingPlan('manage');
      const response = await fetch(`${getApiBase()}/api/subscriptions/portal`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-user-id': user.id,
        },
        body: JSON.stringify({
          returnUrl: window.location.href,
        }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to open billing portal');
      }

      const { url } = await response.json();
      window.location.href = url;
    } catch (err: any) {
      console.error('Portal failed:', err);
      setError(err.message || 'Failed to open billing portal.');
      setProcessingPlan(null);
    }
  };

  const handleChangePlan = async (newPlanSlug: string, newBillingCycle: 'monthly' | 'annual') => {
    if (!user || !activeSub) return;

    try {
      setProcessingPlan(newPlanSlug);
      setError(null);

      const response = await fetch(`${getApiBase()}/api/subscriptions/change-plan`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-user-id': user.id,
        },
        body: JSON.stringify({
          newPlanSlug,
          billingCycle: newBillingCycle,
        }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to change plan');
      }

      const data = await response.json();
      
      // Show success message and reload data
      setError(null);
      await loadData(); // Reload to get updated subscription data
      
      // Navigate back with appropriate message based on upgrade/downgrade
      if (data.isUpgrade) {
        navigate('/member-credits?updated=true&type=upgrade');
      } else {
        // Downgrade scheduled for end of period
        navigate(`/member-credits?updated=true&type=downgrade&effectiveDate=${encodeURIComponent(data.scheduledChange?.effectiveDate || '')}`);
      }
    } catch (err: any) {
      console.error('Plan change failed:', err);
      setError(err.message || 'Failed to change plan. Please try again.');
    } finally {
      setProcessingPlan(null);
    }
  };

  const handleCancelSubscription = async () => {
    if (!user || !activeSub) return;

    if (!window.confirm('Are you sure you want to cancel your subscription? It will remain active until the end of your current billing period.')) {
      return;
    }

    try {
      setProcessingPlan('cancel');
      setError(null);

      const response = await fetch(`${getApiBase()}/api/subscriptions/cancel`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-user-id': user.id,
        },
        body: JSON.stringify({
          cancelAtPeriodEnd: true,
        }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to cancel subscription');
      }

      // Show success message and reload data
      setError(null);
      await loadData(); // Reload to get updated subscription data
      
      // Navigate back to member credits page
      navigate('/member-credits?cancelled=true');
    } catch (err: any) {
      console.error('Cancellation failed:', err);
      setError(err.message || 'Failed to cancel subscription. Please try again.');
    } finally {
      setProcessingPlan(null);
    }
  };

  const formatPrice = (price: number) => {
    return price % 1 === 0 ? `$${price}` : `$${price.toFixed(2)}`;
  };

  const formatCredits = (num: number | undefined) => {
    if (num === undefined || num === null) return '0';
    return num.toLocaleString('en-US');
  };

  // Iris-voiced descriptions per plan slug (10-16 words).
  // New slugs: free / basic / pro / corporate. Legacy slugs
  // (starter/studio/team/agency) are kept as fallbacks so older plan rows
  // coming from the DB still render gracefully rather than hard-breaking.
  const getIrisDescription = (slug: string): string => {
    const map: Record<string, string> = {
      // Current plan tiers
      free:       "Try everything with no cost — a great way to see what I can do.",
      basic:      "Perfect if you're just getting started building your first AI workflows.",
      pro:        "This is my go-to recommendation — serious power for serious creators.",
      corporate:  "Maximum scale for teams and high-volume work — built for the big builds.",
      // Legacy slugs (graceful fallback)
      starter:    "Perfect if you're just getting started building your first AI workflows.",
      studio:     "For creators who push limits daily and need credits that keep up.",
      team:       "Built for teams — collaborate, create, and scale without running dry.",
      agency:     "Maximum scale for high-volume work — I'd save this for the big builds.",
    };
    return map[slug] ?? map[slug?.toLowerCase()] ?? "Unlock your creative potential with Knods AI generation.";
  };

  if (loading) {
    return (
      <div className="subscription-page">
        <div className="subscription-loading">
          <div className="spinner" />
          <p>Loading plans...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="subscription-page">
      <PageHeader />
      <div className="subscription-container">
        {/* Header */}
        <div className="subscription-header">
          <h1>{isManaging ? 'Manage Your Subscription' : 'Unlock Your Creative Power'}</h1>
          <p className="subtitle">
            {isManaging 
              ? 'Upgrade, downgrade, or cancel your current subscription' 
              : 'Unlock the full power of AI workflows with a subscription plan'
            }
          </p>

          {/* Billing Toggle */}
          <div className="billing-toggle">
            <button
              className={`toggle-option ${billingCycle === 'monthly' ? 'active' : ''}`}
              onClick={() => setBillingCycle('monthly')}
            >
              Monthly
            </button>
            <button
              className={`toggle-option ${billingCycle === 'annual' ? 'active' : ''}`}
              onClick={() => setBillingCycle('annual')}
            >
              Annual
              <span className="save-badge">Save 17%</span>
            </button>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="error-banner">
            <p>{error}</p>
            <button onClick={() => setError(null)}>Dismiss</button>
          </div>
        )}

        {/* Active Subscription Banner */}
        {(() => {
          console.log('[SubscriptionPlans] Banner check:', { 
            activeSub, 
            hasActiveSub: !!activeSub, 
            planSlug: activeSub?.planSlug,
            isNotFree: activeSub?.planSlug !== 'free',
            shouldShowBanner: activeSub && activeSub.planSlug !== 'free'
          });
          return null;
        })()}

        {/* Plans Grid */}
        <div className="plans-grid">
          {plans.map(plan => {
            const isCurrentPlan = activeSub?.planSlug === plan.slug;
            const isFreePlan = plan.slug === 'free';
            const isFreeTier = !activeSub || activeSub.planSlug === 'free';
            const price = billingCycle === 'monthly' 
              ? plan.monthlyPrice 
              : plan.annualMonthlyEquivalent;
            const totalAnnual = plan.annualPrice;

            console.log(`[SubscriptionPlans] Plan: ${plan.slug}`, {
              isCurrentPlan,
              isFreePlan,
              activeSubPlanSlug: activeSub?.planSlug,
              planSlug: plan.slug,
              comparison: `${activeSub?.planSlug} === ${plan.slug}`,
            });

            // Check if this plan has a scheduled change to it
            const urlParams = new URLSearchParams(window.location.search);
            const scheduledChangeType = urlParams.get('type');
            const effectiveDate = urlParams.get('effectiveDate');
            const isScheduledTarget = scheduledChangeType === 'downgrade' && !isCurrentPlan && effectiveDate;

            return (
              <div
                key={plan.id}
                className={`plan-card ${plan.isFeatured ? 'featured' : ''} ${isCurrentPlan ? 'current' : ''} ${isFreePlan ? 'free-tier' : ''}`}
              >
                {plan.isFeatured && (
                  <div className="featured-badge">
                    <StarIcon fontSize="small" />
                    Most Popular
                  </div>
                )}

                {isCurrentPlan && !isFreePlan && (
                  <div className="current-badge">Current Plan</div>
                )}

                {isScheduledTarget && (
                  <div className="current-badge" style={{ background: 'var(--color-accent-primary)' }}>
                    Scheduled {new Date(effectiveDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                  </div>
                )}

                {isFreePlan && isFreeTier && user && (
                  <div className="current-badge free-badge">Your Plan</div>
                )}

                <h2 className="plan-name">{plan.name === 'Agency' ? 'Enterprise' : plan.name}</h2>

                {/* Credits hero — main differentiator */}
                <div className="plan-credits-hero">
                  <span className="plan-credits-amount">{formatCredits(plan.monthlyCredits)}</span>
                  <span className="plan-credits-unit">credits/mo</span>
                </div>

                {/* Price — secondary */}
                <div className="plan-pricing">
                  {isFreePlan ? (
                    <span className="price free-price">Free</span>
                  ) : (
                    <>
                      <span className="price">{formatPrice(price)}</span>
                      <span className="period">/mo</span>
                    </>
                  )}
                </div>

                {billingCycle === 'annual' && !isFreePlan && (
                  <p className="annual-total">{formatPrice(totalAnnual)} billed annually</p>
                )}

                {isFreePlan && (
                  <p className="annual-total">No credit card required</p>
                )}

                {/* Iris chat bubble — hidden on free plan */}
                {!isFreePlan && (
                  <div className="plan-iris-quote-wrapper">
                    <img src="/icons/iris-butterfly.svg" alt="Iris" className="plan-iris-quote-avatar" />
                    <div className="plan-iris-quote-bubble">
                      {getIrisDescription(plan.slug)}
                    </div>
                  </div>
                )}

                <button
                  className={`subscribe-button ${plan.isFeatured ? 'featured' : ''} ${isFreePlan ? 'free-button' : ''} ${isCurrentPlan ? 'current-plan-btn' : ''}`}
                  onClick={() => {
                    if (isManaging && activeSub) {
                      // Management mode - handle plan changes
                      if (isCurrentPlan) {
                        // Current plan in management mode - show cancel option
                        handleCancelSubscription();
                      } else {
                        // Different plan - change to this plan
                        handleChangePlan(plan.slug, billingCycle);
                      }
                    } else {
                      // Normal subscription flow
                      if (isFreePlan) {
                        if (!user) {
                          navigate('/');
                        } else if (activeSub && activeSub.planSlug !== 'free') {
                          handleManageSubscription();
                        } else {
                          navigate('/');
                        }
                      } else if (isCurrentPlan) {
                        handleManageSubscription();
                      } else if (activeSub && activeSub.planSlug !== 'free') {
                        // User has active subscription - change to new plan directly
                        handleChangePlan(plan.slug, billingCycle);
                      } else {
                        handleSubscribe(plan.slug);
                      }
                    }
                  }}
                  disabled={processingPlan !== null}
                >
                  {processingPlan === plan.slug ? (
                    <>
                      <div className="button-spinner" />
                      Processing...
                    </>
                  ) : isManaging && activeSub ? (
                    // Management mode button text
                    isCurrentPlan ? 'Cancel Subscription' : (
                      isFreePlan ? 'Downgrade to Free' : 'Change to This Plan'
                    )
                  ) : (
                    // Normal mode button text
                    isFreePlan ? (
                      !user ? 'Get Started Free' : (activeSub && activeSub.planSlug !== 'free' ? 'Downgrade to Free' : 'Go to Dashboard')
                    ) : isCurrentPlan ? (
                      'Current Plan'
                    ) : activeSub && activeSub.planSlug !== 'free' ? (
                      'Switch to This Plan'
                    ) : (
                      'Subscribe Now'
                    )
                  )}
                </button>
              </div>
            );
          })}
        </div>

        {/* Iris byline — below the plans */}
        {!isManaging && (
          <div className="pricing-iris-byline">
            <img src="/icons/iris-butterfly.svg" alt="Iris" className="pricing-iris-icon" />
            <span>Iris's picks for every creative level</span>
          </div>
        )}

        {/* Back Button for Management Mode */}
        {isManaging && (
          <div className="back-to-account">
            <button 
              className="back-button"
              onClick={() => navigate('/member-credits')}
            >
              ← Back to Account
            </button>
          </div>
        )}

        {/* Footer */}
        <div className="subscription-footer">
          <p>
            {isManaging 
              ? 'Upgrades apply immediately. Downgrades take effect at the end of your billing period.'
              : 'All plans include access to all AI models. Credits reset monthly. Cancel anytime.'
            }
          </p>
          <p className="payment-note">Secure payment powered by Stripe</p>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionPlans;
